/**
 * Convert an input audio Blob to a WAV (PCM) Blob using the browser's decoder.
 *
 * Why: some native recorders (notably Android) produce AAC/ADTS which OpenAI Whisper
 * rejects. Browsers can usually decode AAC and we can re-encode to WAV which Whisper supports.
 */

export async function convertAudioBlobToWavBlob(input: Blob): Promise<Blob> {
  const arrayBuffer = await input.arrayBuffer();

  const AudioContextCtor: typeof AudioContext | undefined =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (globalThis as any).AudioContext ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (globalThis as any).webkitAudioContext;

  if (!AudioContextCtor) {
    throw new Error("AudioContext is not available in this environment");
  }

  const audioContext = new AudioContextCtor();
  try {
    const decoded = await audioContext.decodeAudioData(arrayBuffer.slice(0));
    const wavBuffer = audioBufferToWav(decoded);
    return new Blob([wavBuffer], { type: "audio/wav" });
  } finally {
    // Avoid leaking native audio resources
    try {
      await audioContext.close();
    } catch {
      // ignore
    }
  }
}

function audioBufferToWav(audioBuffer: AudioBuffer): ArrayBuffer {
  const numChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;

  // We'll encode 16-bit PCM.
  const format = 1; // PCM
  const bitDepth = 16;
  const bytesPerSample = bitDepth / 8;

  const numFrames = audioBuffer.length;
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataByteLength = numFrames * blockAlign;
  const buffer = new ArrayBuffer(44 + dataByteLength);
  const view = new DataView(buffer);

  // RIFF header
  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + dataByteLength, true);
  writeString(view, 8, "WAVE");

  // fmt chunk
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true); // PCM chunk size
  view.setUint16(20, format, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);

  // data chunk
  writeString(view, 36, "data");
  view.setUint32(40, dataByteLength, true);

  // Interleave and write samples
  let offset = 44;
  const channelData: Float32Array[] = [];
  for (let c = 0; c < numChannels; c++) {
    channelData.push(audioBuffer.getChannelData(c));
  }

  for (let i = 0; i < numFrames; i++) {
    for (let c = 0; c < numChannels; c++) {
      const sample = clamp(channelData[c][i], -1, 1);
      // Convert float [-1,1] to signed 16-bit
      const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
      view.setInt16(offset, intSample, true);
      offset += 2;
    }
  }

  return buffer;
}

function writeString(view: DataView, offset: number, str: string) {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}
